import appLogger from '@/utils/logger';
import { toast } from 'react-toastify';

/**
 * The message the API put in an error response body, or null when there is none. Unlike
 * extractErrorMessage it never falls back to the client-side text (e.g. axios's "Request failed
 * with status code 400"), so a caller can show its own wording when the server said nothing.
 *
 * Handles a raw AxiosError (`error.response.data`) and an unwrapped AxiosResponse (`error.data`,
 * produced by an interceptor doing Promise.reject(error.response)).
 */
export const extractServerErrorMessage = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') return null;
  const err = error as Record<string, any>;

  if (err.response?.data && typeof err.response.data === 'object') {
    const msg = extractFromDataObject(err.response.data);
    if (msg) return msg;
  }
  if (err.data && typeof err.data === 'object') {
    const msg = extractFromDataObject(err.data);
    if (msg) return msg;
  }
  return null;
};

/**
 * Unified error message extraction for the entire frontend.
 *
 * Handles every error shape that can appear:
 * - Raw AxiosError (`error.response.data` plus optional top-level `error.data` alias from axios interceptor)
 * - Legacy unwrapped Axios response body (`error.data` only)
 * - Error instances
 * - Plain strings
 * - Plain objects with message/error property
 */
export const extractErrorMessage = (
  error: unknown,
  defaultMessage = 'An unexpected error occurred',
): string => {
  if (!error) return defaultMessage;

  // Plain string
  if (typeof error === 'string') {
    return error || defaultMessage;
  }

  if (typeof error !== 'object') return defaultMessage;

  const err = error as Record<string, any>;

  const serverMessage = extractServerErrorMessage(error);
  if (serverMessage) return serverMessage;

  // Error instance
  if (error instanceof Error) {
    return error.message || defaultMessage;
  }

  // Plain object with message or error property
  if ('message' in err && err.message) {
    return String(err.message);
  }
  if ('error' in err && err.error && typeof err.error === 'string') {
    return err.error;
  }

  return defaultMessage;
};

/** Pull a human-readable message out of an API response data object. */
function extractFromDataObject(data: Record<string, any>): string | null {
  if (!data || typeof data !== 'object') return null;

  const details = data.details;
  if (typeof details === 'string' && details.trim()) return details;
  if (Array.isArray(details) && details.length > 0) {
    const first = details[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object') {
      const piece = first.message ?? first.msg ?? first.error ?? first.path;
      if (typeof piece === 'string') return piece;
    }
    try {
      return JSON.stringify(details);
    } catch {
      return String(details.length);
    }
  }
  if (details && typeof details === 'object' && !Array.isArray(details)) {
    const nested = details.message ?? details.error;
    if (typeof nested === 'string') return nested;
  }

  if (typeof data.message === 'string' && data.message.trim()) return data.message;
  if (typeof data.error === 'string' && data.error.trim()) return data.error;

  // Nested error objects (e.g. some OAuth / provider payloads)
  if (data.error && typeof data.error === 'object') {
    const nested = data.error as Record<string, unknown>;
    const nestedMsg = nested.message;
    if (typeof nestedMsg === 'string') return nestedMsg;
    const errorsArr = nested.errors;
    if (Array.isArray(errorsArr) && errorsArr.length > 0) {
      const first = errorsArr[0];
      if (first && typeof first === 'object' && 'message' in first && typeof (first as { message: unknown }).message === 'string') {
        return (first as { message: string }).message;
      }
    }
  }

  return null;
}

/**
 * Extract error message and display as a toast notification.
 * Returns the extracted message string.
 */
export const handleApiError = (error: unknown, defaultMessage = 'An unexpected error occurred'): string => {
  const message = extractErrorMessage(error, defaultMessage);
  toast.error(message);
  return message;
};

/**
 * Display a success toast notification.
 */
export const handleApiSuccess = (message: string): void => {
  toast.success(message);
};

/**
 * Log an API/analytics error with structured context.
 * Replaces the per-module logAnalyticsError pattern.
 */
export const logApiError = (
  context: string,
  err: unknown,
  additionalData?: Record<string, unknown>,
): void => {
  const errorMessage = extractErrorMessage(err);
  const errorStack = err instanceof Error ? err.stack : undefined;

  appLogger.error(`Error ${context}:`, {
    data: {
      error: err,
      message: errorMessage,
      stack: errorStack,
      ...additionalData,
    },
  });
};
